import React from "react";
import PageFrame from "./PageFrame";
import { Phone, Mail, Globe, MapPin, ArrowRight } from "lucide-react";
import { ASSETS, REALTOR } from "../lib/brand";

export default function AboutMePage({ pageNum, totalPages, onDownload }) {
    return (
        <PageFrame pageNum={pageNum} totalPages={totalPages} label="Meet JJ">
            <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 py-16 sm:py-24 w-full">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                    {/* Photo */}
                    <div className="lg:col-span-5">
                        <div className="aspect-square overflow-hidden border border-hairline bg-sand">
                            <img
                                src={ASSETS.familyPhoto}
                                alt="JJ Botelho with family on the Emerald Coast"
                                className="w-full h-full object-cover object-center"
                            />
                        </div>
                        <p className="serif-display italic text-2xl text-navy/80 mt-4 leading-snug">
                            "{REALTOR.tagline}"
                        </p>
                    </div>

                    {/* Bio + Contact */}
                    <div className="lg:col-span-7">
                        <div className="eyebrow">Meet your Emerald Coast expert</div>
                        <h2 className="serif-display text-6xl sm:text-7xl text-navy mt-3 mb-4">
                            {REALTOR.name}
                        </h2>

                        <div className="flex flex-wrap gap-2 mb-8">
                            <Chip>REALTOR®</Chip>
                            <Chip>Loan Originator</Chip>
                            <Chip>Florida Panhandle Local</Chip>
                        </div>

                        <div className="space-y-5 text-ink/90 text-lg leading-relaxed">
                            <p>
                                I didn't just move to the Emerald Coast; I was shaped by it. I was raised on these beaches, and today, I'm raising my six-year-old twin girls here. They're my <em>'why'</em> and the reason I do things differently.
                            </p>
                            <p>
                                Most people find the home-buying process a mess of middle-men. I fixed that. By serving as both your <strong className="text-navy">REALTOR®</strong> and your <strong className="text-navy">Mortgage Loan Originator</strong>, I offer a true one-stop shop. It's white-glove service without the hand-offs.
                            </p>
                            <p>
                                Whether you're PCSing to Eglin or Hurlburt, buying your first home, or looking to invest in a rental property, I know these six cities inside and out. To me, this isn't just a market — it's the place I call home, and I'd be honored to help you do the same.
                            </p>
                        </div>

                        {/* Dual contact card */}
                        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 border border-hairline">
                            <div className="p-6 border-b md:border-b-0 md:border-r border-hairline bg-white">
                                <div className="eyebrow mb-3">Real Estate</div>
                                <div className="serif-display text-2xl text-navy">{REALTOR.companyRealty}</div>
                                <a href={`mailto:${REALTOR.emailRealty}`} className="mt-3 flex items-center gap-2 text-ink/90 hover:text-coral break-all">
                                    <Mail size={14} className="flex-shrink-0" />
                                    <span className="text-sm">{REALTOR.emailRealty}</span>
                                </a>
                                <div className="mt-2 flex items-start gap-2 text-muted2 text-sm">
                                    <MapPin size={14} className="flex-shrink-0 mt-0.5" />
                                    <span>{REALTOR.address}</span>
                                </div>
                            </div>
                            <div className="p-6 bg-sand/60">
                                <div className="eyebrow mb-3">Mortgage</div>
                                <div className="serif-display text-2xl text-navy">{REALTOR.companyMortgage}</div>
                                <a href={`mailto:${REALTOR.emailMortgage}`} className="mt-3 flex items-center gap-2 text-ink/90 hover:text-coral break-all">
                                    <Mail size={14} className="flex-shrink-0" />
                                    <span className="text-sm">{REALTOR.emailMortgage}</span>
                                </a>
                                <div className="mt-2 text-muted2 text-xs tracking-[0.12em] uppercase">
                                    NMLS #{REALTOR.nmlsPersonal} · Co. NMLS #{REALTOR.nmlsCompany}
                                </div>
                            </div>
                        </div>

                        {/* CTAs */}
                        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <a
                                href={`tel:${REALTOR.phoneTel}`}
                                data-testid="contact-phone-btn"
                                className="group inline-flex items-center justify-center gap-3 bg-navy text-cream py-4 px-6 rounded-sm font-sans text-xs font-semibold tracking-[0.22em] uppercase transition-all duration-300 hover:bg-navy-hover hover:-translate-y-0.5"
                            >
                                <Phone size={14} />
                                Call · {REALTOR.phone}
                            </a>
                            <a
                                href={REALTOR.websiteUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contact-apply-btn"
                                className="group inline-flex items-center justify-center gap-3 border border-navy text-navy py-4 px-6 rounded-sm font-sans text-xs font-semibold tracking-[0.22em] uppercase transition-all duration-300 hover:bg-navy hover:text-cream hover:-translate-y-0.5"
                            >
                                <Globe size={14} />
                                Apply at JJ-Botelho.com
                                <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                            </a>
                        </div>

                        <button
                            onClick={onDownload}
                            data-testid="about-download-btn"
                            className="mt-3 w-full sm:w-auto text-coral hover:text-coral-hover text-xs tracking-[0.22em] uppercase font-semibold transition-colors py-2"
                        >
                            ↓ Download a branded PDF copy of this guide
                        </button>
                    </div>
                </div>
            </div>
        </PageFrame>
    );
}

function Chip({ children }) {
    return (
        <span className="inline-block border border-navy/20 text-navy bg-cream px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase font-sans font-semibold">
            {children}
        </span>
    );
}
